<template>
  <div class="tw-relative" :data-test-id="testId">
    <label
      class="tw-flex tw-items-center"
      :class="labelClasses"
      :data-test-id="`${testId}-label`"
    >
      <input
        v-model="proxy"
        type="checkbox"
        class="tw-peer tw-sr-only"
        :disabled="readonly || disabled"
        :data-test-id="`${testId}-input`"
      />
      <div
        class="tw-peer tw-relative tw-rounded-full after:tw-absolute after:tw-start-[2px] after:tw-top-[2px] after:tw-rounded-full after:tw-bg-white after:tw-transition-all after:tw-content-[''] peer-checked:after:tw-translate-x-full peer-checked:after:tw-border-white peer-focus:tw-outline-none"
        :class="indicatorClasses"
      />
      <span
        class="tw-text-sm tw-font-medium"
        :class="
          disabled ? 'tw-text-primary-grey-200' : 'tw-text-primary-grey-700'
        "
      >
        <slot />
      </span>
    </label>
  </div>
</template>

<script setup lang="ts">
import { toRefs, computed } from "vue";

export type Size = "sm" | "md";

export interface ToggleProps {
  value: boolean;
  size?: Size;
  disabled?: boolean;
  readonly?: boolean;
  testId?: string;
}

const props = withDefaults(defineProps<ToggleProps>(), {
  value: false,
  size: "md",
  disabled: false,
  readonly: false,
  testId: "toggle",
});

const { value } = toRefs(props);

const proxy = computed({
  get() {
    return value.value;
  },
  set(value: boolean) {
    emit("update:value", value);
  },
});

const emit = defineEmits<{
  "update:value": [value: boolean];
}>();

const sizes: Record<Size, string> = {
  sm: "tw-h-4 tw-w-7 after:tw-h-3 after:tw-w-3",
  md: "tw-h-6 tw-w-11 after:tw-h-5 after:tw-w-5",
};

const indicatorClasses = computed(() => {
  if (props.disabled || props.readonly) {
    return `${sizes[props.size]} peer-checked:tw-bg-primary-grey-200 tw-bg-primary-grey-200`;
  }
  return `${sizes[props.size]} peer-checked:tw-bg-system-success-500 tw-bg-primary-grey-300`;
});

const labelClasses = computed(() => {
  if (props.disabled) {
    return "tw-cursor-default";
  }
  if (props.readonly) {
    return "tw-cursor-not-allowed";
  }
  return "tw-cursor-pointer ";
});
</script>
